import { createContext, useContext, type MouseEvent, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaChevronDown, FaChevronUp, FaEnvelope, FaExclamationTriangle, FaPhoneAlt } from "react-icons/fa";
import GenderIcon from "../../components/GenderIcon";
import CopyButton from "../../components/CopyButton";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../../components/lds/LdsTable";
import {
  childNameWithAge,
  compactIban,
  warningLabel,
  type FinanceBlock,
  type FinanceParent,
  type FinanceStudentRow,
} from "./financeStudentsColumns";
import { type ContributionOverview, type ContributionRow, placementsByReceipt } from "./contributionsModel";
import { ContributionProgress, FamilyPayments, MonthStrip, OpenMonths } from "./ContributionDetails";
import { AdjustmentChips, paidOfText } from "./Adjustments";
import type { FamilyOption } from "./FamilyPicker";

// "Per child" view of the contributions page: every child of the school year with its parents (the former
// "Children and parents" report), and with access to contributions a progress bar and the payments per child.

/** Which detail pages the viewer may open; the list itself does not require any of them. */
export type DetailAccess = { child: boolean; parent: boolean; family: boolean };
const DetailAccessContext = createContext<DetailAccess>({ child: false, parent: false, family: false });

/** Contribution status per child, when the viewer may see contributions. */
type ContributionAccess = {
  rows: Map<string, ContributionRow>;
  expanded: Set<string>;
  toggle: (childId: string) => void;
} | null;
const ContributionContext = createContext<ContributionAccess>(null);

/** Links and buttons inside a clickable row must not also trigger the row. */
const stopRowClick = (event: MouseEvent) => event.stopPropagation();

const linkClassName = "hover:text-indigo-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded-sm dark:hover:text-indigo-300 print:no-underline print:text-inherit";

const printStyles = `
  .finance-students-print-only { display: none; }
  @media print {
    @page { size: A4 landscape; margin: 8mm; }
    .finance-students-no-print { display: none !important; }
    .finance-students-print-only { display: block !important; }
    .finance-students-page { padding: 0 !important; }
    .finance-students-page > div { max-width: none !important; }
    .finance-students-page > div > div { border: 0 !important; box-shadow: none !important; padding: 0 !important; }
    .finance-students-card { border: 0 !important; box-shadow: none !important; border-radius: 0 !important; }
    .finance-students-scroll { overflow: visible !important; }
    .finance-students-table { font-size: 7.5pt !important; }
    .finance-students-table th, .finance-students-table td { padding: 2px 4px !important; font-size: 7.5pt !important; }
    .finance-students-table thead { display: table-header-group; }
    .finance-students-table tr { break-inside: avoid; page-break-inside: avoid; }
  }
`;

export default function ChildView({
  rows, blocks, detailAccess, contributions, canManage, expanded, onToggle, onOpenChild, onChanged, familyOptions,
}: {
  rows: FinanceStudentRow[];
  blocks: FinanceBlock[];
  detailAccess: DetailAccess;
  /** Contributions of the school year; null without access (then a row click opens the child, as before). */
  contributions: ContributionOverview | null;
  canManage: boolean;
  expanded: Set<string>;
  onToggle: (childId: string) => void;
  onOpenChild?: (childId: string) => void;
  onChanged: () => void;
  familyOptions: FamilyOption[];
}): ReactElement {
  const { t } = useTranslation();
  const contributionRows = new Map((contributions?.rows ?? []).map((row) => [row.childId, row]));
  const families = new Map((contributions?.families ?? []).map((family) => [family.familyId, family]));
  const canExpand = contributions !== null;
  const access: ContributionAccess = canExpand ? { rows: contributionRows, expanded, toggle: onToggle } : null;
  const firstWithdrawnIndex = rows.findIndex((row) => row.status === "WITHDRAWN");
  const columnCount = blocks.length || 1;

  return (
    <>
      <style>{printStyles}</style>
      <LdsTableScroll className="finance-students-scroll">
        <LdsTable className="finance-students-table w-full text-xs">
          <LdsTableHead>
            <LdsTableRow>
              {blocks.map((block) => (
                <LdsTableHeadCell key={block.group} className="!whitespace-normal !px-3 !py-3 text-[10px]">
                  {block.group}
                </LdsTableHeadCell>
              ))}
            </LdsTableRow>
          </LdsTableHead>
          <DetailAccessContext.Provider value={detailAccess}>
          <ContributionContext.Provider value={access}>
          <LdsTableBody>
            {rows.length > 0 ? rows.flatMap((row, index) => {
              const open = canExpand && expanded.has(row.childId);
              const onRowClick = canExpand
                ? () => onToggle(row.childId)
                : onOpenChild ? () => onOpenChild(row.childId) : undefined;
              const contribution = contributionRows.get(row.childId);
              const cells = (
                <LdsTableRow
                  key={row.childId}
                  data-status={row.status}
                  data-child={row.childId}
                  onClick={onRowClick}
                  className={[
                    row.status === "WITHDRAWN" ? "bg-slate-50/80 dark:bg-white/[0.03]" : "",
                    open ? "!bg-indigo-50/40 dark:!bg-white/[0.04]" : "",
                    onRowClick ? "cursor-pointer transition hover:bg-indigo-50/60 dark:hover:bg-white/[0.05] print:cursor-auto" : "",
                  ].join(" ")}
                >
                  {blocks.map((block) => (
                    <LdsTableCell key={block.group} className={blockCellClassName(block)}>
                      <BlockCell block={block} row={row} />
                    </LdsTableCell>
                  ))}
                </LdsTableRow>
              );
              const details = open ? (
                <LdsTableRow key={`${row.childId}-details`} data-details={row.childId} className="finance-students-no-print bg-indigo-50/40 dark:bg-white/[0.04]">
                  <LdsTableCell colSpan={columnCount} className="!px-4 !pb-4 !pt-1">
                    <ChildContribution
                      row={contribution}
                      siblings={contribution?.familyId ? (contributions?.rows ?? []).filter((other) => other.familyId === contribution.familyId) : []}
                      contributions={contributions}
                      familyName={contribution?.familyId ? families.get(contribution.familyId)?.familyName : undefined}
                      canManage={canManage}
                      onChanged={onChanged}
                      familyOptions={familyOptions}
                    />
                  </LdsTableCell>
                </LdsTableRow>
              ) : null;
              const rowWithDetails = details ? [cells, details] : [cells];
              if (index !== firstWithdrawnIndex) return rowWithDetails;
              return [
                <LdsTableRow key="withdrawn-divider" className="finance-students-divider">
                  <LdsTableCell
                    colSpan={columnCount}
                    className="!bg-slate-100 !px-3 !py-2 text-[11px] font-bold uppercase tracking-wide !text-slate-600 dark:!bg-white/[0.06] dark:!text-slate-300"
                  >
                    {t("finance.students_report.withdrawn_divider", {
                      count: rows.length - firstWithdrawnIndex,
                      defaultValue: "Withdrawn ({{count}})",
                    })}
                  </LdsTableCell>
                </LdsTableRow>,
                ...rowWithDetails,
              ];
            }) : (
              <LdsTableRow>
                <LdsTableCell colSpan={columnCount} className="py-8 text-center text-slate-500">
                  {t("finance.students_report.empty", { defaultValue: "No children found." })}
                </LdsTableCell>
              </LdsTableRow>
            )}
          </LdsTableBody>
          </ContributionContext.Provider>
          </DetailAccessContext.Provider>
        </LdsTable>
      </LdsTableScroll>
    </>
  );
}

function blockCellClassName(block: FinanceBlock): string {
  const base = "!px-3 !py-3 !text-xs align-top break-words";
  if (block.kind === "child") return `${base} min-w-[11rem]`;
  if (block.kind === "dates") return `${base} whitespace-nowrap`;
  if (block.kind === "parent") return `${base} min-w-[10rem]`;
  return `${base} max-w-[14rem]`;
}

const has = (block: FinanceBlock, key: string) => block.columns.some((column) => column.key === key);
const column = (block: FinanceBlock, suffix: string) => block.columns.find((value) => value.key.endsWith(`.${suffix}`));

function BlockCell({ block, row }: { block: FinanceBlock; row: FinanceStudentRow }) {
  switch (block.kind) {
    case "child":
      return <ChildBlock block={block} row={row} />;
    case "dates":
      return <DatesBlock block={block} row={row} />;
    case "parent":
      return <ParentBlock block={block} row={row} parent={row.parents?.[block.parentSlot - 1]} />;
    default:
      return <WarningsBlock row={row} />;
  }
}

function ChildBlock({ block, row }: { block: FinanceBlock; row: FinanceStudentRow }) {
  const { t } = useTranslation();
  const access = useContext(DetailAccessContext);
  const details = block.columns
    .filter((value) => value.key !== "child.name" && value.key !== "family.names")
    .map((value) => value.value(row))
    .filter(Boolean);
  const families = has(block, "family.names") ? (row.families || []).filter((family) => family.name) : [];
  const contributionAccess = useContext(ContributionContext);
  const contribution = contributionAccess?.rows.get(row.childId);
  const open = Boolean(contributionAccess?.expanded.has(row.childId));
  return (
    <div className="space-y-1">
      {has(block, "child.name") && (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm font-bold text-slate-950 dark:text-white">
          <GenderIcon gender={row.gender} className="shrink-0" />
          {access.child ? (
            <Link to={`/family/children/${row.childId}/details`} onClick={stopRowClick} className={linkClassName}>
              {childNameWithAge(row)}
            </Link>
          ) : (
            <span>{childNameWithAge(row)}</span>
          )}
          {row.status === "WITHDRAWN" && (
            <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600 dark:bg-white/10 dark:text-slate-300">
              {t("finance.students_report.status.withdrawn", { defaultValue: "Withdrawn" })}
            </span>
          )}
          {contributionAccess && (
            <button
              type="button"
              aria-expanded={open}
              aria-label={open
                ? t("finance.students_report.hide_contribution", { name: childNameWithAge(row), defaultValue: "Hide contribution of {{name}}" })
                : t("finance.students_report.show_contribution", { name: childNameWithAge(row), defaultValue: "Show contribution of {{name}}" })}
              onClick={(event) => {
                event.stopPropagation();
                contributionAccess.toggle(row.childId);
              }}
              className="finance-students-no-print ml-auto rounded-md p-1 text-[11px] font-normal text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-white/10"
            >
              {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
            </button>
          )}
        </div>
      )}
      {contribution && <ContributionProgress row={contribution} />}
      {contribution && <OpenMonths cells={contribution.cells} />}
      {(details.length > 0 || families.length > 0) && (
        <div className="text-[11px] text-slate-500 dark:text-slate-400">
          {details.join(" · ")}
          {details.length > 0 && families.length > 0 && " · "}
          {families.map((family, index) => (
            <span key={family.familyId}>
              {index > 0 && ", "}
              {access.family ? (
                <Link to={`/family/${family.familyId}/details`} onClick={stopRowClick} className={linkClassName}>
                  {family.name}
                </Link>
              ) : (
                family.name
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function DatesBlock({ block, row }: { block: FinanceBlock; row: FinanceStudentRow }) {
  const entries = block.columns
    .map((value) => ({ key: value.key, label: value.shortLabel, text: value.value(row) }))
    .filter((entry) => entry.text);
  if (entries.length === 0) return <span className="text-slate-400">—</span>;
  return (
    <dl className="grid grid-cols-[auto_auto] gap-x-2 gap-y-0.5 text-[11px]">
      {entries.map((entry) => (
        <div key={entry.key} className="contents">
          <dt className="text-slate-500 dark:text-slate-400">{entry.label}</dt>
          <dd className={`tabular-nums ${entry.key === "child.firstStart" ? "font-bold text-slate-950 dark:text-white" : "text-slate-700 dark:text-slate-200"}`}>
            {entry.text}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ParentBlock({ block, row, parent }: { block: FinanceBlock; row: FinanceStudentRow; parent?: FinanceParent }) {
  const { t } = useTranslation();
  const access = useContext(DetailAccessContext);
  if (!parent) return <span className="text-slate-400">—</span>;
  const name = [parent.firstName, parent.lastName].filter(Boolean).join(" ");
  const email = column(block, "email") ? parent.email || "" : "";
  const phone = column(block, "phone") ? parent.phone || "" : "";
  const address = column(block, "address")?.value(row) || "";
  const holder = column(block, "accountHolder") ? parent.accountHolderName || "" : "";
  const iban = column(block, "iban") ? parent.iban || "" : "";
  return (
    <div className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-1">
        {column(block, "name") && (
          access.parent && parent.parentId ? (
            <Link to={`/family/parent/${parent.parentId}/details`} onClick={stopRowClick}
              className={`font-semibold text-slate-900 dark:text-white ${linkClassName}`}>
              {name || "—"}
            </Link>
          ) : (
            <span className="font-semibold text-slate-900 dark:text-white">{name || "—"}</span>
          )
        )}
        {email && <ContactButton kind="email" value={email} name={name} />}
        {phone && <ContactButton kind="phone" value={phone} name={name} />}
      </div>
      {phone && (
        <div className="flex items-center gap-1 text-[11px] text-slate-700 dark:text-slate-200">
          <span className="tabular-nums">{phone}</span>
          <CopyButton
            value={phone}
            label={t("common.copy_phone", { defaultValue: "Copy phone number" })}
            className="!h-5 !w-5 text-[10px] print:hidden"
          />
        </div>
      )}
      {email && (
        <div className="flex items-center gap-1 text-[11px]">
          <a
            href={`mailto:${email}`}
            onClick={stopRowClick}
            title={t("finance.students_report.mail_to", { name: name || email, defaultValue: "Email {{name}}" })}
            className="break-all text-indigo-600 hover:underline print:text-slate-900 print:no-underline dark:text-indigo-300"
          >
            {email}
          </a>
          <CopyButton
            value={email}
            label={t("common.copy_email", { defaultValue: "Copy email address" })}
            className="!h-5 !w-5 text-[10px] print:hidden"
          />
        </div>
      )}
      {address && <div className="text-[11px] text-slate-500 dark:text-slate-400">{address}</div>}
      {(holder || iban) && (
        <div className="text-[11px] text-slate-600 dark:text-slate-300">
          {holder && <span>{holder}</span>}
          {holder && iban && <span> · </span>}
          {iban && <span className="font-mono" title={iban}>{compactIban(iban)}</span>}
        </div>
      )}
    </div>
  );
}

/** Small mail/call button next to the parent's name; hidden on paper, where the values are printed below. */
function ContactButton({ kind, value, name }: { kind: "email" | "phone"; value: string; name: string }) {
  const { t } = useTranslation();
  const href = kind === "email" ? `mailto:${value}` : `tel:${value.replace(/[^+\d]/g, "")}`;
  const label = kind === "email"
    ? t("finance.students_report.mail_to", { name: name || value, defaultValue: "Email {{name}}" })
    : t("finance.students_report.call", { name: name || value, defaultValue: "Call {{name}}" });
  return (
    <a
      href={href}
      onClick={stopRowClick}
      title={label}
      aria-label={label}
      className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-slate-200 text-[11px] text-indigo-600 transition hover:border-indigo-300 hover:bg-indigo-50 print:hidden dark:border-white/10 dark:text-indigo-300 dark:hover:bg-white/10"
    >
      {kind === "email" ? <FaEnvelope aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
    </a>
  );
}

function WarningsBlock({ row }: { row: FinanceStudentRow }) {
  const { t } = useTranslation();
  const warnings = row.warnings || [];
  if (warnings.length === 0) return null;
  return (
    <span className="flex flex-col gap-1">
      {warnings.map((warning) => (
        <span key={warning} className="inline-flex items-start gap-1 text-[11px] font-semibold leading-tight text-amber-700 dark:text-amber-300">
          <FaExclamationTriangle aria-hidden="true" className="mt-0.5 shrink-0" />
          {warningLabel(t, warning)}
        </span>
      ))}
    </span>
  );
}


/** Contribution of one child when its row is opened: the months on one line and the family's payments. */
export function ChildContribution({ row, siblings, contributions, familyName, canManage, onChanged, familyOptions }: {
  row?: ContributionRow;
  siblings: ContributionRow[];
  contributions: ContributionOverview | null;
  familyName?: string | null;
  canManage: boolean;
  onChanged: () => void;
  familyOptions: FamilyOption[];
}) {
  const { t } = useTranslation();
  if (!row || !contributions) {
    return (
      <p className="text-sm text-slate-500">
        {t("finance.students_report.no_contribution", { defaultValue: "No contribution for this child in this school year." })}
      </p>
    );
  }
  return (
    <div className="grid gap-3 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-start lg:gap-6">
      <div className="grid gap-1.5">
        <MonthStrip cells={row.cells} currentMonth={contributions.currentMonth} showAmounts />
        <span className="text-[11px] text-slate-600 dark:text-slate-300">
          {paidOfText(t, row, contributions.monthlyFee, contributions.currentMonth, contributions.excludeCurrentMonth)}
        </span>
        <AdjustmentChips adjustments={row.adjustments} canAdjust={false} onEdit={() => undefined} />
      </div>
      {row.familyId && contributions.schoolYear ? (
        <FamilyPayments
          family={{ familyId: row.familyId, familyName: familyName ?? row.familyName }}
          schoolYearId={contributions.schoolYear.id}
          canManage={canManage}
          onChanged={onChanged}
          childOptions={siblings.map((sibling) => ({ childId: sibling.childId, name: [sibling.firstName, sibling.lastName].filter(Boolean).join(" ") }))}
          defaultChildId={row.childId}
          familyOptions={familyOptions}
          months={contributions.months}
          placements={placementsByReceipt(siblings)}
        />
      ) : (
        <p className="text-amber-700 dark:text-amber-300">{t("finance.students_report.warnings.no_family", { defaultValue: "No family linked" })}</p>
      )}
    </div>
  );
}
